// frontend/src/lib/consent.js
import { supabase } from "../../supabaseClient";

export const CONSENT_PURPOSES = Object.freeze({
  OPTIONAL_ANALYTICS:
    "optional_analytics",

  ENHANCED_PERSONALISATION:
    "enhanced_personalisation",

  ENHANCED_READINESS_PROFILING:
    "enhanced_readiness_profiling",

  AI_PERSONALISATION:
    "ai_personalisation",

  MARKETING_EMAIL:
    "marketing_email",

  MARKETING_SMS:
    "marketing_sms",

  MARKETING_WHATSAPP:
    "marketing_whatsapp",

  PARTNER_PROGRESS_SHARING:
    "partner_progress_sharing",

  ADVISOR_PROGRESS_SHARING:
    "advisor_progress_sharing",

  FUNDER_REFERRAL:
    "funder_referral",

  SOFT_CREDIT_CHECK:
    "soft_credit_check",

  FINANCIAL_DATA_INTEGRATION:
    "financial_data_integration",
});

const CONSENT_CACHE_TTL_MS =
  30_000;

const consentCache = new Map();

function normaliseArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function dispatchConsentChange(
  detail
) {
  if (
    typeof window ===
    "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(
      "gwin:consent-changed",
      {
        detail,
      }
    )
  );
}

function consentCacheKey(
  userId,
  purposeKey
) {
  return `${userId}::${purposeKey}`;
}

function isAuthenticationError(
  error
) {
  const status = Number(
    error?.status ||
      error?.statusCode ||
      0
  );

  const code = String(
    error?.code || ""
  ).toUpperCase();

  const message = String(
    error?.message ||
      error?.error_description ||
      ""
  ).toLowerCase();

  return (
    status === 401 ||
    code === "PGRST301" ||
    code === "PGRST303" ||
    message.includes("jwt expired") ||
    message.includes("invalid jwt") ||
    message.includes(
      "not authenticated"
    ) ||
    message.includes(
      "refresh token"
    )
  );
}

async function getConsentSession() {
  try {
    const {
      data,
      error,
    } =
      await supabase.auth.getSession();

    if (error) {
      if (
        !isAuthenticationError(
          error
        )
      ) {
        console.error(
          "Could not read the current consent session:",
          error
        );
      }

      return null;
    }

    return data?.session || null;
  } catch (error) {
    if (
      !isAuthenticationError(error)
    ) {
      console.error(
        "Could not read the current consent session:",
        error
      );
    }

    return null;
  }
}

export function clearConsentCache(
  purposeKey = null
) {
  if (!purposeKey) {
    consentCache.clear();
    return;
  }

  for (
    const key of
    consentCache.keys()
  ) {
    if (
      key === purposeKey ||
      key.endsWith(
        `::${purposeKey}`
      )
    ) {
      consentCache.delete(key);
    }
  }
}

export async function getMyConsentGateStatus() {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_consent_gate_status"
  );

  if (error) throw error;

  return {
    requiresReview:
      data?.requires_review ===
      true,

    isInitialSetup:
      data?.is_initial_setup ===
      true,

    pendingDocuments:
      normaliseArray(
        data?.pending_documents
      ),

    pendingPurposes:
      normaliseArray(
        data?.pending_purposes
      ),
  };
}

export async function completeMyConsentReview({
  optionalChoices,
  source,
}) {
  const effectiveSource =
    source || "onboarding";

  const {
    data,
    error,
  } = await supabase.rpc(
    "complete_my_consent_review",
    {
      p_optional_choices:
        optionalChoices || {},

      p_source:
        effectiveSource,
    }
  );

  if (error) throw error;

  clearConsentCache();

  dispatchConsentChange({
    type:
      "review_completed",

    source:
      effectiveSource,

    optionalChoices:
      optionalChoices || {},
  });

  return data;
}

export async function getMyPrivacySettings() {
  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_privacy_settings"
  );

  if (error) throw error;

  if (Array.isArray(data)) {
    return {
      purposes: data,
      documents: [],
    };
  }

  return {
    purposes: normaliseArray(
      data?.purposes ||
        data?.permissions ||
        data?.consent_purposes
    ),

    documents: normaliseArray(
      data?.documents ||
        data?.legal_documents
    ),
  };
}

export async function setMyConsent(
  purposeKey,
  granted,
  source = "profile"
) {
  if (!purposeKey) {
    throw new Error(
      "A consent purpose is required."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "set_my_consent",
    {
      p_purpose_key:
        purposeKey,

      p_granted:
        granted === true,

      p_source:
        source,
    }
  );

  if (error) throw error;

  const result =
    Array.isArray(data)
      ? data[0]
      : data;

  clearConsentCache(
    purposeKey
  );

  dispatchConsentChange({
    type:
      "permission_changed",

    purposeKey,

    granted:
      granted === true,

    status:
      result?.status ||
      null,

    source,
  });

  return result;
}

export async function hasMyConsent(
  purposeKey,
  {
    forceRefresh = false,
  } = {}
) {
  if (!purposeKey) {
    return false;
  }

  /*
   * Check authentication before making
   * the protected RPC request.
   */
  const session =
    await getConsentSession();

  const userId =
    session?.user?.id;

  if (
    !userId ||
    !session?.access_token
  ) {
    return false;
  }

  /*
   * Include the user ID in the cache key.
   * This prevents one user's consent result
   * from being reused after another user
   * signs into the same browser.
   */
  const cacheKey =
    consentCacheKey(
      userId,
      purposeKey
    );

  const cached =
    consentCache.get(cacheKey);

  const now = Date.now();

  if (
    !forceRefresh &&
    cached &&
    now - cached.checkedAt <
      CONSENT_CACHE_TTL_MS
  ) {
    return (
      cached.allowed === true
    );
  }

  try {
    const {
      data,
      error,
    } = await supabase.rpc(
      "has_my_consent",
      {
        p_purpose_key:
          purposeKey,
      }
    );

    if (error) {
      throw error;
    }

    const allowed =
      data === true;

    consentCache.set(
      cacheKey,
      {
        allowed,
        checkedAt: now,
      }
    );

    return allowed;
  } catch (error) {
    /*
     * A 401 can occur during logout,
     * session expiry or token refresh.
     * This is an expected fail-closed
     * situation and does not need to be
     * reported as an application error.
     */
    if (
      !isAuthenticationError(
        error
      )
    ) {
      console.error(
        `Could not verify consent for ${purposeKey}:`,
        error
      );
    }

    consentCache.set(
      cacheKey,
      {
        allowed: false,
        checkedAt: now,
      }
    );

    return false;
  }
}

export async function runWithConsent(
  purposeKey,
  action,
  {
    onDenied,
    forceRefresh = false,
  } = {}
) {
  if (
    typeof action !==
    "function"
  ) {
    throw new Error(
      "A consent-controlled action is required."
    );
  }

  const allowed =
    await hasMyConsent(
      purposeKey,
      {
        forceRefresh,
      }
    );

  if (!allowed) {
    if (
      typeof onDenied ===
      "function"
    ) {
      return onDenied();
    }

    return {
      allowed: false,
      reason:
        "consent_not_granted",
    };
  }

  return action();
}